import React, { useRef, useEffect } from "react";
import { AniOsBrandingWatermark } from "@/components/branding/AniOsBrandingLayer";
import { useOsWindowList, useOsActions, useOsFocus } from "./OsContext";
import { OsWindowById } from "./OsWindow";

const OsDesktopComponent: React.FC = () => {
  const { windowIdsByWorkspace, workspaces, activeWorkspaceId } = useOsWindowList();
  const { switchWorkspace } = useOsActions();
  const { focusedWindowId } = useOsFocus();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeWorkspaceIndexRef = useRef(0);
  const isProgrammaticScrollRef = useRef(false);
  const [containerWidth, setContainerWidth] = React.useState(0);

  // Track container width changes
  useEffect(() => {
    const updateWidth = () => {
      if (scrollContainerRef.current) {
        setContainerWidth(scrollContainerRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    
    // Use ResizeObserver for more accurate detection
    const resizeObserver = new ResizeObserver(updateWidth);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateWidth);
      resizeObserver.disconnect();
    };
  }, []);

  // Update active workspace index when it changes
  useEffect(() => {
    const activeIndex = workspaces.findIndex((ws) => ws.id === activeWorkspaceId);
    activeWorkspaceIndexRef.current = activeIndex;
  }, [activeWorkspaceId, workspaces]);

  // Scroll to active workspace when it changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      const activeIndex = workspaces.findIndex((ws) => ws.id === activeWorkspaceId);
      const containerWidth = scrollContainerRef.current.clientWidth;
      const targetScroll = activeIndex * containerWidth;
      
      isProgrammaticScrollRef.current = true;
      scrollContainerRef.current.scrollTo({
        left: targetScroll,
        behavior: "instant",
      });
      
      // Reset flag after a short delay to allow scroll to complete
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 100);
    }
  }, [activeWorkspaceId, workspaces]);

  // Handle scroll snap to detect workspace changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = () => {
      // Ignore scroll events triggered by programmatic scrolling
      if (isProgrammaticScrollRef.current) return;
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollLeft = container.scrollLeft;
        const containerWidth = container.clientWidth;
        const newIndex = Math.round(scrollLeft / containerWidth);
        
        if (newIndex !== activeWorkspaceIndexRef.current && workspaces[newIndex]) {
          switchWorkspace(workspaces[newIndex].id);
        }
      }, 50);
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      clearTimeout(scrollTimeout);
      container.removeEventListener("scroll", handleScroll);
    };
  }, [workspaces, switchWorkspace]);

  // Only enable horizontal scrolling if there are multiple workspaces
  const hasMultipleWorkspaces = workspaces.length > 1;

  return (
    <div 
      ref={scrollContainerRef}
      className={`relative w-full h-full overflow-y-hidden no-scrollbar ${
        hasMultipleWorkspaces ? "overflow-x-auto" : "overflow-x-hidden"
      }`}
      style={{
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        // Only enable scroll snap if there are multiple workspaces
        scrollSnapType: hasMultipleWorkspaces ? "x mandatory" : "none",
        scrollSnapStop: "normal",
        overscrollBehavior: "none",
      }}
    >
      <div className="flex h-full">
        {workspaces.map((workspace) => {
          const windowIds = windowIdsByWorkspace.get(workspace.id) || [];
          const isActiveWorkspace = workspace.id === activeWorkspaceId;

          return (
            <div
              key={workspace.id}
              className="os-desktop-container relative h-full flex-shrink-0"
              style={{
                width: containerWidth || '100%',
                scrollSnapAlign: 'start',
              }}
            >
              <div
                className="pointer-events-none absolute inset-0 z-[1] overflow-visible"
                data-testid="anios-branding-watermark-layer"
              >
                <AniOsBrandingWatermark />
              </div>

              <div
                className="absolute inset-0 z-[2]"
                data-testid="os-desktop-window-bounds"
              >
                {windowIds.map((windowId) => (
                  <OsWindowById
                    key={windowId}
                    windowId={windowId}
                    isActive={isActiveWorkspace}
                    focusedWindowId={focusedWindowId}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const OsDesktop = React.memo(OsDesktopComponent);
